/**
 * opal-tooltip-compat.tsx
 * Wraps shadcn Tooltip with the Opal Tooltip API:
 *   <Tooltip tooltip="text" side="top"><button>...</button></Tooltip>
 * instead of the shadcn compound pattern:
 *   <Tooltip><TooltipTrigger>...</TooltipTrigger><TooltipContent>...</TooltipContent></Tooltip>
 *
 * This shim is imported by src/lib/opal/components.tsx to fulfil
 * all @opal/components Tooltip call sites without any consumer changes.
 */
import React from "react";
import {
  Tooltip as ShadcnTooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/shared/ui/components/ui/tooltip";

export interface OpalTooltipProps {
  children: React.ReactNode;
  /** Primary prop: tooltip text/content */
  tooltip?: React.ReactNode;
  /** Legacy alias for tooltip (some call sites used `content=`) */
  content?: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  delayDuration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Legacy alias for side */
  placement?: string;
  /** Legacy alias for delayDuration (ms) */
  delay?: number;
}

/**
 * Opal-compatible Tooltip wrapper around shadcn's Radix Tooltip.
 * When no label is provided, renders children unwrapped (no tooltip).
 */
export function OpalTooltip({
  children,
  tooltip,
  content,
  side = "top",
  align = "center",
  sideOffset = 4,
  delayDuration,
  open,
  onOpenChange,
  placement,
  delay,
}: OpalTooltipProps) {
  const label = tooltip ?? content;
  const effectiveSide =
    (placement as "top" | "bottom" | "left" | "right" | undefined) ?? side;
  const effectiveDelay = delayDuration ?? (delay !== undefined ? delay : 400);

  if (!label) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={effectiveDelay}>
      <ShadcnTooltip open={open} onOpenChange={onOpenChange}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={effectiveSide} align={align} sideOffset={sideOffset}>
          {label}
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );
}
